// Imports
import React, { useCallback, useRef } from 'react';
import Heading from '../../../shareables/foundation/admin-ui/components/content/heading';
import Box from '../../../shareables/foundation/admin-ui/components/content/box';
import ActivityLogTable from './table';
import PageActions from '../../../shareables/foundation/admin-ui/components/content/page-actions';
import PageAction from '../../../shareables/foundation/admin-ui/components/content/page-action';
import { faCloudArrowDown } from '@fortawesome/pro-regular-svg-icons';
import formatForCSV from '../../../shareables/utils/format-for-csv';
import { saveAs } from 'file-saver';
import { GetActivitiesResponse } from '../../../shareables/types/api/main/reseller/activities/get';


// Define the accepted props
interface Props {
	initialActivities?: GetActivitiesResponse;
	storybookPageSize?: number;
}


// Function component
const ActivityLog: React.FC<Props> = ({ initialActivities, storybookPageSize }) => {
	// Use ref
	const allActivities = useRef<GetActivitiesResponse | null>(null);
	
	
	// Handle activities change
	const handleActivitiesChange = useCallback(
		(activities: GetActivitiesResponse) => (allActivities.current = activities),
		[]
	);
	
	
	// Handle export
	const handleExport = () => {
		// Do no work if data is loading
		if (allActivities.current === null) {
			return;
		}
		
		
		// Initialize
		let csvContent = `Date,Action,Details,User\n`;
		
		
		// Loop over activities and add to CSV content
		allActivities.current.forEach((activity) => {
			csvContent += formatForCSV(activity.occurred);
			
			csvContent += `,${formatForCSV(activity.action)}`;
			
			csvContent += `,${formatForCSV(activity.details)}`;
			
			csvContent += `,${formatForCSV(activity.user ? `${activity.user.firstName} ${activity.user.lastName}` : 'None')}`;
			
			csvContent += '\n';
		});
		
		
		// Download file
		saveAs(new Blob([csvContent], { type: 'text/csv;charset=utf-8' }), 'activities.csv');
	};
	
	return (
		<React.Fragment>
			<Heading size={1}>Activity log</Heading>
			
			<PageActions>
				<PageAction
					onClick={handleExport}
					icon={faCloudArrowDown}
					text='Export activities'
					tooltip='Export the list of activities'
				/>
			</PageActions>
			
			<Box>
				<p>
					View a history of actions that have been completed in your reseller account, along with who completed each
					action. This is helpful for tracing how certain outcomes occurred. The history is kept for 2 years.
				</p>
			</Box>
			
			<ActivityLogTable
				pageSizes={[10, 25, 50, 100]}
				storybookPageSize={storybookPageSize}
				initialActivities={initialActivities}
				onActivitiesChange={handleActivitiesChange}
			/>
		</React.Fragment>
	);
};

export default ActivityLog;
